import { IPersonaProps, IDropdownOption, Stack, IStackTokens, TextField, PrimaryButton, IButtonStyles, mergeStyles, Spinner, SpinnerSize, FontIcon, Dropdown, ResponsiveMode, DatePicker, IconButton } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { IFile } from "../../../../../interfaces/IFile";
import { IFilePanelInfo } from "../../../../../interfaces/IFilePanelInfo";
import { isMobile, mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { useLazyGetDriveInfoFromSharepointIdQuery } from "../../../redux/api/graph/api-graph-slice";
import { RootState } from "../../../redux/storeMain";
import { PeopleFinder } from "../../../_shared/PeopleFinder/PeopleFinder";
import { Fileview } from "../../Explorer/Fileview/Fileview";
import { ItemLink, HookType, IHookResult, SignRequestRecipient, SignRequestResult, ApprovalRequest } from "../../../redux/api/local/api-local-interfaces";
import { SuccessErrorMessage } from "../../../_shared/SuccessErrorMessage/SuccessErrorMessage";
import { getFileItemIdFromRelativePath, getFileItemInfo, getItem, getItemApprovalRequestInfo, getItemWebViewUrl } from "../../../../../services/GraphService";
import { IDriveInfo } from "../../../../../interfaces/IDriveInfo";
import authModule from "../../../../../../../utilities/AuthModule";
import { ComponentTypes } from "../../../../../config/config";
import { splitName } from "../../../../helpers/stringHelper";
import { apiGetHooksByType, apiRequestApproval } from "../../../../../services/ApiService";
import { sharePointInternalFileName } from "../../../../../config/constants";
import { getValidHooks } from "../../../../../services/HooksService";
import { DateService } from "../../../../../services/dateService";
import { DayPickerStrings, firstDayOfWeek } from "../../../../../config/dateSettings";
import dayjs from "dayjs";
import { clearButtonClass } from "../../../../../config/controlStyles";
import { clearButtonProps, datePickerStyles, iconButtonProps, iconButtonStyles, textFieldStyles } from "../../../../../styles/datePickerStyles";

const panelActionButtonStyle: Partial<IButtonStyles> = {
  root: {
    marginTop: 20,
  },
};

export interface IFileApproval extends IFile {
  itemId?: string
  folderId?: string;
  serverRelativePath?: string;
}

export interface ISigningPanelProps {
  file?: IFilePanelInfo;
  files?: IFileApproval[];
  tenantEnablePII?: boolean;
  tenantGuid?: string;
  requestor?: string;
  isContentManager?: boolean;
  closeClicked: () => void;
  listName?: string;
}

const stackTokens: IStackTokens = { childrenGap: 20 };

const minItems = 5;

const openSigningRequestWindow = (signRequestResults: SignRequestResult) => {
  const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
  const winTop = window.screenTop ? window.screenTop : window.screenY;

  const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  const left = Math.max(0, ((width / 2) - (600 / 2)) + winLeft);
  const top = Math.max(0, ((height / 2) - (800 / 2)) + winTop);

  window.open(signRequestResults.designerUrl, "Docubird.Signing", `width=800, height=600, top=${top}, left=${left}, scrollbars=yes`);
}

export const SigningPanel = ({ file, files, tenantEnablePII = false, tenantGuid, requestor, isContentManager, closeClicked, listName }: ISigningPanelProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const user = useSelector((state: RootState) => state.user);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);

  const [getDriveInfoFromSharepointId] = useLazyGetDriveInfoFromSharepointIdQuery();

  const [fileViews, setFileViews] = React.useState<JSX.Element[]>([]);
  const [isBusySending, setIsBusySending] = React.useState(false);
  const [formDisabled, setFormDisabled] = React.useState(true);
  const [otherError, setOtherError] = React.useState('');
  const [isSuccessRequestSigning, setIsSuccessRequestSigning] = React.useState(false);
  const [isErrorRequestSigning, setIsErrorRequestSigning] = React.useState(false);
  const [errorRequestSigning, setErrorRequestSigning] = React.useState();
  const [hooks, setHooks] = React.useState<IHookResult[]>([]);
  const [selectedFlow, setSelectedFlow] = React.useState<IHookResult>()
  const [options, setOptions] = React.useState<IDropdownOption[]>();
  const [signingFiles, setSigningFiles] = React.useState<IFileApproval[]>();
  const [additionalDetails, setAdditionalDetails] = React.useState<string>('');
  const [dueDate, setDueDate] = React.useState<Date>();
  const [dateFormat] = React.useState<Intl.DateTimeFormat>(new Intl.DateTimeFormat());
  const [showMoreIsClicked, setShowMore] = React.useState<boolean>(false);
  const [sectionStyle, setSectionStyle] = React.useState<any>();
  const [selectedRecipients, setSelectedRecipients] = React.useState<SignRequestRecipient[]>([])

  const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder);

  React.useEffect(() => {
    (async () => {
      const hooks = await apiGetHooksByType({
        TenantGUID: tenantGuid ?? user.account.tenantId,
        HookTypeId: HookType.Sign
      });

      const validHooks = getValidHooks(hooks, currentDirectory);

      setHooks(validHooks);

      if (validHooks.length === 1) {
        setSelectedFlow(validHooks[0]);
      }

      setOptions(validHooks.map(hook => {
        return {
          key: hook.id,
          text: hook.name
        }
      }));
    })();
  }, []);

  React.useEffect(() => {
    if (signingFiles) {
      setFileViews(
        signingFiles?.map((item, index) => (
          <div key={index}>
            <Fileview
              item={item}
              isMobile={isMobile()}
              multiSelect={false}
              hasShowMore={false}
              tenantEnablePII={tenantEnablePII}
              nonClickable={true}
            />
          </div>
        ))
      );
    }
  }, [signingFiles]);

  React.useEffect(() => {
    let tempFiles: IFileApproval[] = [];
    if (file) {
      tempFiles.push({
        ...file
      })
      setSigningFiles(tempFiles);
    }
    else {
      setSigningFiles(files);
    }
  }, []);

  React.useEffect(() => {
    let isDisabled = false;
    if (!selectedFlow) {
      isDisabled = true;
    } else if (selectedRecipients?.length === 0) {
      isDisabled = true;
    }

    setFormDisabled(isDisabled);
  }, [selectedFlow, selectedRecipients]);

  React.useEffect(() => {
    const itemHeight = isMobile() ? mobileListItemStyle.height : 40;
    let containerHeight = itemHeight * signingFiles?.length;

    if (!showMoreIsClicked) {
      containerHeight = signingFiles?.length > minItems ? itemHeight * minItems : itemHeight * signingFiles?.length;
    }

    setSectionStyle(
      mergeStyles({
        maxHeight: containerHeight,
        overflow: "hidden",
        transition: "max-height 0.5s ease-in-out",
      })
    );
  }, [showMoreIsClicked, signingFiles]);

  const selectedPersonasChanged = (selectedPersonas: IPersonaProps[]) => {
    setSelectedRecipients(selectedPersonas.map(persona => {
      let { firstName, lastName } = splitName(persona.text);
      return {
        email: persona.secondaryText,
        firstName,
        lastName
      }
    }));
  }

  const sendClicked = async () => {
    setIsBusySending(true);
    setIsErrorRequestSigning(false);
    setIsSuccessRequestSigning(false);
    setErrorRequestSigning(null);
    setOtherError('');
    const tempFiles = [...signingFiles];

    const links: ItemLink[] = [];
    let hasError = false;
    const result = await Promise.all(

      tempFiles.map(async tempFile => {
        let itemId = '';
        let driveId = '';

        if (!isContentManager) {
          driveId = tempFile.driveId;
          if (tempFile.id && !tempFile.itemId && !tempFile.hasGraphItemId) {

            const driveInfoResults = await getDriveInfoFromSharepointId({ request: { driveId, sharepointId: tempFile.id }, accessToken: await authModule.getGraphAccessToken() }).unwrap();

            if (driveInfoResults.value.length > 0) {
              itemId = driveInfoResults.value[0].id;
            }
            else {
              const driveInfo: IDriveInfo = !tempFile.folderId ? { id: driveId, driveId: null } : { id: tempFile.folderId, driveId }

              const fileItem = await getFileItemInfo(driveInfo, tempFile.name, "id,webUrl");
              if (fileItem) {
                itemId = fileItem.id;
              }
            }
          }
          else if (tempFile.hasGraphItemId) {
            itemId = tempFile?.id;
          }
          else {
            //source: Advanced Search
            itemId = tempFile.itemId;
            if (!itemId) {
              itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, tempFile["serverRelativePath"]);
            }
          }
        } else {
          //source: Content Manager
          driveId = isInOneDrive.current ? oneDriveRootDetails?.driveId : (currentDirectory?.parentId ? currentDirectory?.parentId : currentDirectory?.id);

          const spItemUrlparts = tempFile['.spItemUrl'].split('/');
          itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];

          if (!itemId) {
            const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
            const folderUrl = new URL(driveUrl.origin + tempFile['FileRef']);
            itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
          }
        }

        if (itemId) {
          try {
            const fileDetails = await getItemApprovalRequestInfo(driveId, itemId);

            const link: ItemLink = {
              name: tempFile.name || tempFile["Name"] || tempFile[sharePointInternalFileName] || "",
              fullWebUrl: fileDetails?.listItem?.webUrl,
              uniqueId: fileDetails?.sharepointIds?.listItemUniqueId,
              siteUrl: fileDetails?.sharepointIds?.siteUrl,
              listId: fileDetails?.sharepointIds?.listId,
              listName: listName || currentDirectory?.DocumentLibrary || (await getItem(await authModule.getGraphAccessToken(), driveId, null))?.name,
              serverRelativeUrl: tempFile["FileRef"],
              itemId: tempFile["ID"] || fileDetails?.sharepointIds?.listItemId
            }

            links.push(link);

            Promise.resolve(link);
          } catch (error) {
            hasError = true;
            setOtherError(languageStrings.SigningRequestError);
            Promise.reject(languageStrings.SigningRequestError);
          }
        }
      })
    );

    if (result && !hasError) {
      const signingRequest: ApprovalRequest = {
        hookId: selectedFlow.id,
        hookTypeId: HookType.Sign,
        hookName: selectedFlow.name,
        tenantGuid: tenantGuid ? tenantGuid : user.account.tenantId,
        accountId: user.account.accountId,
        requestor: requestor ? requestor : user.account.username,
        approvers: selectedRecipients.map(x => x.email),
        dueDate: dueDate ?? (selectedFlow.dueDateDays > 0 ? dayjs().add(selectedFlow.dueDateDays, "day").toDate() : null),
        // message: additionalDetails,
        details: additionalDetails,
        // documents: links
        itemLinks: links,
      }

      try {
        await apiRequestApproval(signingRequest);
        setIsSuccessRequestSigning(true);
        setIsBusySending(false);
      } catch (error) {
        setIsErrorRequestSigning(true);
        setIsBusySending(false);
        error?.response ? setErrorRequestSigning(error?.response) : setOtherError(languageStrings.SigningRequestError);
      }

    } else {
      setIsBusySending(false);
    }
  }

  const showMore = signingFiles?.length > minItems && (
    <div
      className="xb-show-more-action"
      onClick={() => setShowMore((prevShowMoreIsClicked) => !prevShowMoreIsClicked)}
      style={{ marginTop: "5px" }}
    >
      <FontIcon iconName={!showMoreIsClicked ? "ChevronDown" : "ChevronUp"} />
      <span>{!showMoreIsClicked ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
    </div>
  );

  const flowChanged = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    setSelectedFlow(hooks.find(x => x.id === item.key));
  }

  const onFormatDate = React.useCallback((date?: Date): string => {
    return !date || date?.getTime() !== date?.getTime() ? "" : dateFormat.format(date);
  }, [dateFormat]);

  const onClearClick = (ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!ev.defaultPrevented) {
      setDueDate(undefined);
      ev.stopPropagation();
      ev.preventDefault();
    }
  }

  return (
    <>
      <div style={{ marginLeft: -15, marginRight: -15 }}>
        <div className={sectionStyle} style={{ paddingTop: "15px" }}>
          {fileViews}
        </div>
      </div>
      {showMore}
      <Stack tokens={stackTokens} style={{ marginTop: '15px' }}>

        <Dropdown
          disabled={isSuccessRequestSigning || isBusySending}
          required
          placeholder={languageStrings.SelectOption}
          label={"Sign request name"}
          options={options}
          className="xb-sp-input"
          // responsiveMode={ResponsiveMode.small}
          selectedKey={options && options.length === 1 ? options[0].key : selectedFlow?.id}
          onChange={(event, item) => flowChanged(event, item)}
        />

        <PeopleFinder disabled={isSuccessRequestSigning || isBusySending} label={selectedFlow?.peoplePickerTitle?.trim() || languageStrings.Recipients} allowExternalUsers={true} itemLimit={selectedFlow?.peoplePickerItemLimit} selectedPersonasChanged={selectedPersonasChanged} />

        {Number.isInteger(selectedFlow?.dueDateDays) &&
          <DatePicker
            label={languageStrings.DueDate}
            disabled={isSuccessRequestSigning || isBusySending}
            allowTextInput
            parseDateFromString={DateService.parseDateFromString}
            firstDayOfWeek={firstDayOfWeek}
            strings={DayPickerStrings}
            minDate={dayjs().add(1, "day").toDate()}
            placeholder={selectedFlow.dueDateDays > 0 ? languageStrings.DueDateDescription.replace("{days}", selectedFlow.dueDateDays.toString()) : languageStrings.EnterDate}
            ariaLabel={languageStrings.DueDate}
            showMonthPickerAsOverlay={true}
            onSelectDate={setDueDate}
            formatDate={onFormatDate}
            textField={{
              onRenderSuffix: () => {
                return (
                  <div className={clearButtonClass}>
                    <IconButton
                      styles={iconButtonStyles}
                      iconProps={iconButtonProps}
                      {...clearButtonProps}
                      onClick={onClearClick}
                    />
                  </div>)
              },
              styles: textFieldStyles
            }}
            styles={datePickerStyles}
          />}

        <TextField disabled={isSuccessRequestSigning || isBusySending} label={languageStrings.Message} multiline rows={3} onChange={(event, newValue) => setAdditionalDetails(newValue)} />

        {(isSuccessRequestSigning || isErrorRequestSigning || otherError) && <Stack.Item>
          <SuccessErrorMessage
            isSuccess={isSuccessRequestSigning}
            isError={isErrorRequestSigning || otherError?.length > 1}
            successMessage={languageStrings.SigningRequestCreated}
            errorMessage={otherError?.length > 1 ? otherError : languageStrings.SigningRequestError}
            errorList={errorRequestSigning ? (errorRequestSigning as any).data.failedFileNames : null}
          />
        </Stack.Item>}

        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
          {!isSuccessRequestSigning && <PrimaryButton
            text={languageStrings.Send}
            styles={panelActionButtonStyle}
            iconProps={{ iconName: 'send' }}
            onClick={() => sendClicked()}
            disabled={isBusySending || formDisabled}
          >
            {isBusySending && <Spinner size={SpinnerSize.small} />}
          </PrimaryButton>}
          {isSuccessRequestSigning && <PrimaryButton
            text={languageStrings.Close}
            styles={panelActionButtonStyle}
            iconProps={{ iconName: 'Checkmark' }}
            onClick={() => closeClicked()}
          />}
        </Stack>
      </Stack>
    </>
  )
};